<template>
  <div>
    <div
        v-show="showHeader"
        class="list-control mt-n2 mx-n2 pt-2 px-2 mb-2">
      <b-row
          v-if="topBarEnabled"
          class="search-input">
        <b-col
            class="mb-1"
            :md="searchWidgetWidth">
          <b-form-group class="mb-0">
            <b-input-group>
              <b-input-group-prepend
                  v-if="categories.length > 0"
                  class="category-filter">
                <b-form-select
                    v-model="selectedCategory"
                    :options="categories"
                    text-field="name"
                    value-field="id">
                  <template #first>
                    <option
                        class="placeholder"
                        :value="null">
                      {{ 'product.category.filter'|trans }}
                    </option>
                  </template>
                </b-form-select>
              </b-input-group-prepend>
              <b-form-input
                  ref="searchField"
                  v-model="searchString"
                  autocomplete="off"
                  :autofocus="selector"
                  :disabled="searchBarDisabled"
                  :placeholder="'frontend.type-to-search'|trans"
                  @keydown.down.prevent.stop="itemDown"
                  @keydown.enter.prevent.stop="emitSelected"
                  @keydown.esc.prevent="clearSearch"
                  @keydown.up.prevent.stop="itemUp"/>
              <b-input-group-append>
                <b-button
                    :disabled="!searchString"
                    variant="outline-secondary"
                    @click="searchString = ''">
                  <i class="far fa-trash-alt"/> {{ 'frontend.clear'|trans }}
                </b-button>
              </b-input-group-append>
            </b-input-group>
          </b-form-group>
        </b-col>

        <b-col
            v-if="!selector"
            class="mb-1"
            md="6">
          <b-form-group
              class="mb-0"
              :label="'frontend.per-page'|trans"
              label-cols-lg="2"
              label-cols-md="3">
            <b-form-select
                v-model="perPage"
                :disabled="rows <= pageOptions[0]"
                :options="pageOptions"/>
          </b-form-group>
        </b-col>
      </b-row>

      <div class="status d-flex flex-wrap">
        <div
            v-show="rows > 0"
            class="align-self-center mr-2 mb-1">
          {{ 'frontend.table.pagination'|trans({start, end, rows}) }}
          <span v-show="!selector && searchString && initialData.length !== rows">
            ({{ 'frontend.table.filtered'|trans }})
          </span>
        </div>

        <div
            v-show="searching && items.length !== 0 && searchString !== ''"
            class="align-self-center mb-1">
          <i class="fas fa-fw fa-circle-notch fa-spin"/>
          {{ 'frontend.searching'|trans }}
        </div>

        <div
            v-show="!isLoading && !hasError && rows > perPage"
            class="ml-auto mb-1">
          <b-pagination
              v-model="currentPage"
              aria-controls="my-table"
              :limit="10"
              :per-page="perPage"
              :total-rows="rows"/>
        </div>
      </div>
    </div>

    <div class="overflow-auto position-relative">
      <StoresTable
          ref="table"
          v-model="displayedItems"
          :busy="isLoading || hasError"
          :current-page="currentPage"
          :error-text="errorText"
          :fields="fields"
          :filter="filter"
          hover
          :is-loading="isLoading"
          :items="items"
          :per-page="perPage"
          :primary-key="resolvedPrimaryKey"
          select-mode="single"
          :selectable="selector"
          show-empty
          small
          :sort-by.sync="sortBy"
          :sort-desc.sync="sortDesc"
          :sort-null-last="sortNullLast"
          :stacked="stacked"
          striped
          @filtered="onFiltered"
          @row-hovered="onRowHover"
          @row-selected="onRowSelected"
          @sort-changed="highlightItem(0)">
        <template #empty>
          <div class="text-center my-2">
            {{ emptyText|trans }}
          </div>
        </template>

        <template #emptyfiltered>
          <div class="text-center my-2">
            <i
                v-if="searchUrl !== ''"
                class="fas fa-fw fa-circle-notch fa-spin"/>
            {{ emptyFilterText|trans }}
          </div>
        </template>

        <template #cell(actions)="data">
          <div class="d-flex justify-content-end">
            <slot
                name="extra_actions"
                v-bind="data"/>
            <b-button
                v-if="editUrl || editShown"
                :href="editUrl|path({item: data.item[identityField]})"
                variant="outline-secondary"
                @click="editClick(data.item)">
              <i class="fas fa-fw fa-edit"/>
              {{ 'form.edit'|trans }}
            </b-button>
            <b-button
                v-if="(removeUrl !== '') || removeShown"
                :href="removeUrl|path({item: data.item[identityField]})"
                variant="outline-danger"
                @click="removeClick(data.item)">
              <i class="fas fa-fw fa-trash-alt"/>
              {{ 'form.remove'|trans }}
            </b-button>
          </div>
        </template>

        <template #cell(thumb)="data">
          <img
              :alt="'product.image'|trans"
              class="table-thumb"
              :src="data.value">
        </template>

        <template #cell(sale_price)="data">
          {{ data.value | currency }}
        </template>

        <template #cell(email_address)="data">
          <a :href="'mailto:' + data.value">{{ data.value }}</a>
        </template>

        <template #cell(website)="data">
          <a
              :href="data.value"
              target="_blank">{{ data.value }}</a>
        </template>

        <template #cell(person_type)="row">
          {{ `person.type.${row.value}`|trans }}
        </template>

        <template #cell(invoice_type)="row">
          {{ 'person.invoice-type.' + row.value | trans }}
        </template>

        <!-- Forward any added slot -->
        <template
            v-for="(_, slot) of $scopedSlots"
            #[slot]="scope">
          <slot
              :name="slot"
              v-bind="scope"/>
        </template>
      </StoresTable>
    </div>
  </div>
</template>

<script lang="ts">
import Search, {SearchResult} from '@/common/search/Search';
import StoresTable from '@/common/StoresTable.vue';
import {Component, Emit, Prop, Ref, Vue, Watch} from 'vue-property-decorator';

@Component({
  components: {
    StoresTable,
  },
})
export default class CommonList extends Vue {
  /** Defines whether the list is loading */
  @Prop({default: true})
  public readonly isLoading!: boolean;

  /** Defines whether the list has an error */
  @Prop({default: false})
  public readonly isErrored!: boolean;

  /** Provides the initial data for the list */
  @Prop({default: () => ([])})
  public readonly initialData!: any[]; // eslint-disable-line @typescript-eslint/no-explicit-any

  /** Defines when the search bar should be disabled */
  @Prop({default: false})
  public readonly searchBarDisabled!: boolean;

  /** Defines whether the list should function as a selector instead of a plain list */
  @Prop({default: false, type: Boolean})
  public readonly selector!: boolean;

  /** Label to be used when searching, only used when the current data set is empty */
  @Prop({default: 'frontend.no-results'})
  public readonly emptyFilterText!: string;

  /** URL to use for searching items */
  @Prop({default: ''})
  public readonly searchUrl!: string;

  /** Label to be used when the current data set is empty */
  @Prop({default: 'frontend.no-results'})
  public readonly emptyText!: string;

  /** Label to be used when an error occured */
  @Prop({default: 'frontend.load-failed'})
  public readonly errorText!: string;

  /** Url to edit an item. 'item'-parameter will be filled with the id of the item */
  @Prop({default: ''})
  public readonly editUrl!: string;

  /** Override showing the edit button */
  @Prop({default: false, type: Boolean})
  public readonly editShown!: boolean;

  /** Url to remove an item. 'item'-parameter will be filled with the id of the item */
  @Prop({default: ''})
  public readonly removeUrl!: string;

  /** Override showing the remove button */
  @Prop({default: false, type: Boolean})
  public readonly removeShown!: boolean;

  /** Default sort variable */
  @Prop({default: ''})
  public readonly defaultSortBy!: string;

  /** Default sort desc variable */
  @Prop({default: false, type: Boolean})
  public readonly defaultSortDesc!: boolean;

  /** Default per page variable */
  @Prop({default: null})
  public readonly defaultPerPage!: number | null;

  @Prop({default: false, type: Boolean})
  public readonly sortNullLast!: boolean;

  /** Disable the first select behaviour */
  @Prop({default: false, type: Boolean})
  public readonly disableFirstSelect!: boolean;

  /** Disable the highlight colouring behaviour */
  @Prop({default: false, type: Boolean})
  public readonly disableHighlightColouring!: boolean;

  /** The fields to be shown in the table */
  @Prop({required: true, type: Array})
  public readonly fields!: any[]; // eslint-disable-line @typescript-eslint/no-explicit-any

  /** Used to uniquely identify passed items */
  @Prop({default: 'id', type: String})
  public readonly identityField!: string;

  /** Different from identity field if the ids in the list are not unique */
  @Prop({type: String})
  public readonly primaryKey?: string;

  /** Provides category data for the list, if applicable */
  @Prop({default: () => ([])})
  public readonly categories!: any[]; // eslint-disable-line @typescript-eslint/no-explicit-any

  @Prop({default: () => ({})})
  public readonly searchOptions!: object;

  @Prop({default: 'md'})
  public readonly stacked!: string | boolean;

  /** List of items to be shown in the table */
  protected items: ListItem[] = [];

  /** The currently displayed items */
  protected displayedItems: ListItem[] = [];

  /** Currently selected item key */
  protected selectedItemKey = 0;

  /** Currently selected item */
  protected selectedItem: ListItem | null = null;

  /** Currently selected category */
  protected selectedCategory: number | null = null;

  /** Local cache of isErrored, in order to be able to raise errors on our own */
  protected hasError = false;
  /** Width in columns of the search widget, depends on whether the perPage input is shown or not */
  protected searchWidgetWidth = 12;
  /** Current string used to filter the list */
  protected searchString = '';
  /** Number of items per page */
  protected perPage = 20;
  /** Options for the number of items per page */
  protected pageOptions: number[] = [10, 20, 50];
  /** Current page number */
  protected currentPage = 1;
  /** Current sort variable */
  protected sortBy = '';
  /** Current sort desc variable */
  protected sortDesc = false;
  /** Search module */
  protected searcher!: Search;
  /** Used to print the emptyFilterText when searching */
  protected filter = '';
  /** Used to debounce the filter handler, as it is triggered when setting the row variant */
  protected debouncedFilter?: string;
  /** When set to true, there is a search running externally */
  protected searching = false;

  @Ref()
  private readonly table!: StoresTable;

  @Ref()
  private readonly searchField!: HTMLInputElement;

  public created(): void {
    this.searchWidgetWidth = this.selector ? 12 : 6;
    this.perPage = this.defaultPerPage ? this.defaultPerPage : 20;
    this.sortBy = this.defaultSortBy;
    this.sortDesc = this.defaultSortDesc;
  }

  // noinspection JSUnusedGlobalSymbols Vue entry point
  public mounted(): void {
    this.searcher = new Search({
      parent: this, // Used to get the events from the search module
    });
    this.searcher.setApiUrl(this.searchUrl);
    this.$on(Search.EVENT_NAME, this.onSearched);
    this.setData();
  }

  /**
   * Focus the search field
   */
  public focusSearchField(empty?: boolean): void {
    if (empty === true) {
      this.searchString = '';
    }

    this.$nextTick(() => {
      if (!this.searchField) {
        return;
      }

      this.searchField.focus();
      this.searchField.select();
    });
  }

  /**
   * Clears the current table selection
   */
  public clearSelected(): void {
    this.table.clearSelected();
  }

  @Emit('search')
  protected emitSearch(): string {
    return this.searchString;
  }

  // noinspection JSUnusedLocalSymbols Used by emit
  @Emit('selected')
  protected emitSelected(): ListItem | null {
    return this.selectedItem;
  }

  @Emit('deselected')
  protected emitDeselected(): void {
    // Just emit
  }

  // noinspection JSUnusedLocalSymbols Used by emit
  @Emit('highlight')
  protected emitHighlight(): ListItem | null {
    return this.selectedItem;
  }

  @Emit('editClick')
  protected editClick(item: ListItem): ListItem {
    return item;
  }

  @Emit('removeClick')
  protected removeClick(item: ListItem): ListItem {
    return item;
  }

  /**
   * Perform search action when the filter string changes
   */
  @Watch('searchString')
  @Watch('selectedCategory')
  @Watch('searchOptions', {deep: true})
  protected search(): void {
    if (this.searchUrl !== '') {
      // The following line is to fake the loading spinner in case of the search url
      // which should on be displayed when there is no data
      this.filter = this.items.length > 0 ? '' : 'true';
      this.searching = true;
      this.searcher?.searchDebounced(this.searchString, Object.assign({}, {
        category: this.selectedCategory,
      }, this.searchOptions), !this.selectedCategory);
    } else {
      this.filter = this.searchString;
    }

    this.emitSearch();
  }

  /**
   * Set the data when the initialData change
   */
  @Watch('initialData')
  protected setData(): void {
    this.items = this.initialData;
    this.items.forEach((i) => Vue.set(i, '_rowVariant', ''));

    this.selectFirstItem();
  }

  /**
   * Copy the isErrored variable when it changes
   */
  @Watch('isErrored')
  protected setErrored(): void {
    this.hasError = this.isErrored;
  }

  /**
   * Highlight the correct item when the selection changes
   */
  protected async highlightItem(newSelectedItemKey: number): Promise<void> {
    await this.$nextTick();

    if (!this.selector || this.disableHighlightColouring) {
      return;
    }

    if (this.displayedItems.length < 1) {
      this.selectedItemKey = 0;
      this.selectedItem = null;

      // Clear all current highlights
      this.items.forEach((item) => item._rowVariant = '');
    } else {
      this.selectedItemKey = newSelectedItemKey;
      this.selectedItem = this.displayedItems[this.selectedItemKey];

      // Update the highlights
      this.items.forEach((item) => {
        item._rowVariant = item[this.resolvedPrimaryKey] === this.selectedItem?.[this.resolvedPrimaryKey] ? 'primary' : '';
      });
    }

    // Let others know which one is highlighted
    this.emitHighlight();
  }

  protected clearSearch(e: Event): void {
    if (this.searchString !== '') {
      e.preventDefault();
      e.stopPropagation();
    }

    this.searchString = '';
  }

  /**
   * Used as callback when search action has been finished
   *
   * @param searchData
   */
  protected onSearched(searchData: SearchResult): void {
    this.searching = false;
    if (searchData.isErrored) {
      this.hasError = true;
      return;
    } else {
      this.hasError = false;
    }

    if (searchData.emptySearch) {
      this.items = this.initialData;
      this.filter = '';
    } else {
      const results = searchData.items;
      if (results) {
        this.filter = '';
      }
      this.items = results || [];
    }

    this.items.forEach((i) => Vue.set(i, '_rowVariant', ''));
    this.selectFirstItem();
  }

  protected onRowHover(item: ListItem, index: number): void {
    this.highlightItem(index);
  }

  /**
   * Emit event when an item has been selected
   */
  protected onRowSelected(items: ListItem[]): void {
    if (!items) {
      return;
    }

    if (items.length > 0) {
      this.emitSelected();
    } else {
      this.emitDeselected();
    }
  }

  /**
   * Used for keyboard operation
   */
  protected itemDown(): void {
    if (!this.selector) {
      return;
    }

    if (this.selectedItemKey < this.displayedItems.length - 1) {
      this.highlightItem(this.selectedItemKey + 1);
    }
  }

  /**
   * Used for keyboard operation
   */
  protected itemUp(): void {
    if (!this.selector) {
      return;
    }

    if (this.selectedItemKey > 0) {
      this.highlightItem(this.selectedItemKey - 1);
    }
  }

  /**
   * Handle filter action (only used when no search url is given)
   */
  protected onFiltered(): void {
    if (this.searchUrl !== '') {
      return;
    }

    // Fix for #201
    // The `onFiltered` is called multiple times (also when only the data changes, and not the filter),
    // so use a filter debounce to make sure only the first invocation of a new filter value triggers
    // the first item to be selected. Ironically, an update to the `_rowVariant` "field" triggers an internal
    // data update, causing this to be executed multiple times when trying to change the highlighted item
    if (this.debouncedFilter === this.filter) {
      return;
    }

    this.debouncedFilter = this.filter;
    this.selectFirstItem();
  }

  /**
   * Select the first item in the list
   */
  private selectFirstItem(): void {
    if (!this.selector || this.disableFirstSelect) {
      return;
    }

    this.highlightItem(0);
  }

  /**
   * Determine the number of rows based on the number of items
   */
  protected get rows(): number {
    if (this.searchUrl === '' && this.filter) {
      return this.displayedItems.length;
    }
    return this.items.length;
  }

  /**
   * Determine the starting item number of the current page
   */
  protected get start(): number {
    return this.perPage * (this.currentPage - 1);
  }

  /**
   * Determine the end item number of the current page
   */
  protected get end(): number {
    return Math.min(this.rows, this.perPage * this.currentPage);
  }

  /**
   * Determine whether the top bar should be enabled
   */
  protected get topBarEnabled(): boolean {
    return !this.searchBarDisabled && this.selector;
  }

  protected get showHeader(): boolean {
    return this.topBarEnabled
        || this.rows > 0
        || (this.searching && this.items.length !== 0 && this.searchString !== '')
        || (!this.isLoading && !this.hasError && this.rows > this.perPage);
  }

  protected get resolvedPrimaryKey(): string {
    return this.primaryKey ?? this.identityField;
  }
}

interface ListItem {
  _rowVariant?: string;

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  [key: string]: any;
}
</script>

<style lang="scss" scoped>
@import "~bootstrap/scss/functions";
@import "~bootstrap/scss/variables";
@import "~bootstrap/scss/mixins/breakpoints";

@include media-breakpoint-down(md) {
  .search-input {
    ::v-deep {
      .category-filter {
        width: 100%;
        margin-bottom: 0.25rem;

        select {
          border-radius: $border-radius;
        }
      }

      .form-control:not(:first-child) {
        border-top-left-radius: $border-radius;
        border-bottom-left-radius: $border-radius;
      }
    }
  }
}

.list-control {
  position: sticky;
  background-color: $white;
  top: 0;
  z-index: 500;
  border-bottom: 1px solid $border-color;

  .status {
    ul {
      margin-bottom: 0;
    }
  }
}
</style>
